import { rampFactor } from '@/domain/rampCurve';
import type { AcquisitionModel, SimulatorState } from '@/domain/types';

type HeadcountInputs = Pick<SimulatorState, 'team' | 'acquisition'>;

/**
 * Effectif (ETP, fractions autorisées) au mois donné.
 *
 * Trois modes selon `team.headcountRamp` :
 * - **Absent** ou `enabled: false` → effectif constant = `team.headcount` (comportement
 *   historique, préservé pour la rétrocompat des états persistés).
 * - **`enabled: true`** → l'effectif évolue de `team.headcount` (valeur de départ) vers
 *   `headcountRamp.targetHeadcount` (plateau) selon **la même courbe paramétrique**
 *   que celle de l'acquisition (`model` + `rampUpMonths`). Cela permet de modéliser
 *   une équipe qui se renforce à mesure que le produit gagne des clients — ou, plus
 *   rarement, qui se瘦身.
 *
 * `headcountThisMonth = headcount + (target − headcount) × rampFactor(t, model)`,
 * avec `t = min(month / rampUpMonths, 1)` (1 au plateau, 0 au démarrage).
 *
 * Pure et déterministe. `month` commence à 1 (cf. boucle de `computeProjection`).
 */
export function headcountAtMonth(month: number, state: HeadcountInputs): number {
  const { team, acquisition } = state;
  const ramp = team.headcountRamp;
  if (!ramp?.enabled) return team.headcount;

  const start = team.headcount;
  const target = ramp.targetHeadcount;
  if (target === start) return start;

  const model: AcquisitionModel = acquisition.model ?? 'constant';
  if (model === 'constant') return target;

  const rampMonths = acquisition.rampUpMonths ?? 0;
  if (rampMonths <= 0) return target;

  const t = Math.min(month / rampMonths, 1);
  return start + (target - start) * rampFactor(t, model);
}
